import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const PAGES = [
  {
    id: "dashboard_content",
    name: "Dashboard Content",
    categories: [
      { id: "global_mistakes", name: "Global Top Mistakes" },
    ]
  },
  {
    id: "reference_guide",
    name: "Reference Guide",
    categories: [
      { id: "taxonomy", name: "Taxonomy" },
    ]
  },
  {
    id: "case_studies",
    name: "Case Studies",
    categories: [
      { id: "case_studies", name: "Case Studies" },
    ]
  },
  {
    id: "sop_guide",
    name: "SOP Guide",
    categories: [
      { id: "sop_guide", name: "SOP Guide" },
    ]
  },
  {
    id: "training",
    name: "Training & Scenarios",
    categories: [
      { id: "scenarios", name: "MCQ Scenarios" },
      { id: "live_call_scenarios", name: "Live Call Scenarios" },
      { id: "form_scenarios", name: "Form Scenarios" },
    ]
  }
];

export default function AdminContent() {
  const [selectedPageId, setSelectedPageId] = useState(PAGES[0].id);
  const [collection, setCollection] = useState(PAGES[0].categories[0].id);
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/content/${collection}`);
      setItems(res.data);
    } catch (err) {
      apiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [collection]);

  const handlePageChange = (pageId) => {
    setSelectedPageId(pageId);
    const page = PAGES.find(p => p.id === pageId);
    if (page && page.categories.length > 0) {
      setCollection(page.categories[0].id);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await api.delete(`/admin/content/${collection}/${id}`);
      toast.success("Item deleted");
      fetchItems();
    } catch (err) {
      apiError(err);
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(JSON.parse(JSON.stringify(item)));
    } else {
      setEditingItem({});
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingItem._id) {
        await api.put(`/admin/content/${collection}/${editingItem._id}`, editingItem);
        toast.success("Item updated");
      } else {
        await api.post(`/admin/content/${collection}`, editingItem);
        toast.success("Item created");
      }
      setIsModalOpen(false);
      fetchItems();
    } catch (err) {
      apiError(err);
    }
  };

  const handleFieldChange = (key, value) => {
    setEditingItem({ ...editingItem, [key]: value });
  };

  const getDynamicFields = () => {
    if (items.length > 0) {
      return Object.keys(items[0]).filter(k => k !== "_id");
    }
    return Object.keys(editingItem || {}).filter(k => k !== "_id");
  };

  const renderFieldValue = (val) => {
    if (typeof val === "object") return JSON.stringify(val);
    return String(val);
  };

  const selectedPage = PAGES.find(p => p.id === selectedPageId);

  return (
    <div className="space-y-6">
      <PageHeader title="Content Management" subtitle="Manage static content across all platform modules" />

      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar Navigation */}
        <div className="w-full md:w-64 shrink-0">
          <Card className="p-4 border-none shadow-sm sticky top-6">
            <h3 className="font-semibold text-slate-800 mb-3 px-2 text-sm uppercase tracking-wider">Pages</h3>
            <div className="space-y-1">
              {PAGES.map(page => (
                <button
                  key={page.id}
                  onClick={() => handlePageChange(page.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${selectedPageId === page.id ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                >
                  {page.name}
                </button>
              ))}
            </div>
          </Card>
        </div>
        
        {/* Main Content Area */}
        <div className="flex-1 space-y-6 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
             <div className="flex flex-wrap gap-2 bg-slate-100 p-1.5 rounded-xl border border-slate-200">
                {selectedPage?.categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setCollection(cat.id)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${collection === cat.id ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`}
                  >
                    {cat.name}
                  </button>
                ))}
             </div>
             <Button onClick={() => openModal()} className="shadow-sm"><Plus className="h-4 w-4 mr-2"/> Add New Item</Button>
          </div>

          <Card className="border-none shadow-sm">
            {loading ? (
              <div className="p-12 text-center text-slate-500 flex flex-col items-center">
                 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                 Loading content...
              </div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center text-slate-500 flex flex-col items-center bg-slate-50/50 rounded-lg">
                 <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                   <Plus className="h-6 w-6 text-slate-400" />
                 </div>
                 No items found in {selectedPage?.categories.find(c => c.id === collection)?.name}.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                    <tr>
                      {getDynamicFields().slice(0, 5).map(f => (
                        <th key={f} className="px-5 py-4 font-semibold capitalize whitespace-nowrap">{f.replace(/_/g, " ")}</th>
                      ))}
                      <th className="px-5 py-4 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item._id} className="border-b border-slate-100 last:border-0 hover:bg-blue-50/30 transition-colors">
                        {getDynamicFields().slice(0, 5).map(f => (
                          <td key={f} className="px-5 py-4 max-w-[240px] truncate text-slate-600 font-medium">
                            {renderFieldValue(item[f])}
                          </td>
                        ))}
                        <td className="px-5 py-4 text-right space-x-2 whitespace-nowrap">
                          <Button variant="outline" size="sm" onClick={() => openModal(item)} className="bg-white hover:bg-slate-50 border-slate-200">
                            <Edit className="h-4 w-4 text-slate-600" />
                          </Button>
                          <Button variant="outline" size="sm" className="bg-white border-slate-200 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(item._id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{editingItem?._id ? "Edit Item" : "New Item"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-5 py-5">
            {editingItem && getDynamicFields().map(key => (
              <div key={key} className="grid gap-2">
                <Label className="capitalize font-semibold text-slate-700">{key.replace(/_/g, " ")}</Label>
                {typeof editingItem[key] === "string" && editingItem[key].length > 50 ? (
                   <Textarea className="resize-y min-h-[100px]" value={editingItem[key] || ""} onChange={e => handleFieldChange(key, e.target.value)} />
                ) : typeof editingItem[key] === "object" ? (
                   <Textarea 
                     className="font-mono text-xs bg-slate-50 min-h-[120px]"
                     value={JSON.stringify(editingItem[key], null, 2)} 
                     onChange={e => {
                       try { handleFieldChange(key, JSON.parse(e.target.value)); }
                       catch(err) { handleFieldChange(key, e.target.value); }
                     }} 
                   />
                ) : (
                  <Input 
                    value={editingItem[key] || ""} 
                    onChange={e => handleFieldChange(key, e.target.value)} 
                  />
                )}
              </div>
            ))}
            {editingItem && !editingItem._id && getDynamicFields().length === 0 && (
              <div className="grid gap-2">
                <Label className="font-semibold text-slate-700">Raw JSON Data</Label>
                <Textarea 
                  className="font-mono text-sm bg-slate-50"
                  placeholder="{}" 
                  rows={12}
                  onChange={e => {
                    try {
                      setEditingItem(JSON.parse(e.target.value));
                    } catch(err) {}
                  }}
                />
                <p className="text-xs text-slate-500">Paste a JSON object with the desired fields to initialize the schema.</p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
